/**
 * OSMSecurityR2Service — Geocodificador L1.12 para infraestructura de seguridad
 *
 * Busca instalaciones de seguridad (Guardia Civil, Policía Local, Policía Nacional)
 * extraídas de OpenStreetMap y almacenadas en R2 mediante fuzzy matching.
 *
 * Datos: OSM amenity=police Andalucía — ~791 features (420 municipios)
 * CRS: EPSG:25830 (UTM30 ETRS89, reproyectado desde WGS84)
 * Ruta R2: /osm-security/{ine5}.json.gz
 * Precisión: ±25 m (datos comunitarios OSM)
 * Licencia: ODbL (Open Database License)
 *
 * DIFERENCIA CLAVE vs otros R2Services:
 * Los tokens uFuzzy están PRE-COMPUTADOS en cada feature (campo "tokens"),
 * por lo que se usan directamente como haystack sin recalcular.
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-21
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  OSMSecurityFeature,
  OSMSecuritySearchOptions,
  OSMSecurityResult,
} from '../../../types/osmSecurity';
import { OSM_SEC_X_RANGE, OSM_SEC_Y_RANGE, OSM_SEC_PRECISION_M } from '../../../types/osmSecurity';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const FETCH_TIMEOUT = 15_000;
const CACHE_MAX_ITEMS = 50;

/** Configuración uFuzzy para nombres españoles de seguridad */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: OSMSecurityR2Service | null = null;

export function getOSMSecurityR2Service(): OSMSecurityR2Service {
  if (!serviceInstance) {
    serviceInstance = new OSMSecurityR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetOSMSecurityR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class OSMSecurityR2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, { data: OSMSecurityFeature[]; ts: number }>();
  private readonly loadingPromises = new Map<string, Promise<OSMSecurityFeature[]>>();
  private readonly fuzzy: uFuzzy;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS POR MUNICIPIO
  // ---------------------------------------------------------------------------

  /** Carga features OSM Security de un municipio desde R2 (con cache LRU) */
  async loadMunicipio(codINE: string): Promise<OSMSecurityFeature[]> {
    if (!/^\d{5}$/.test(codINE)) return [];

    // Cache hit
    const cached = this.cache.get(codINE);
    if (cached) return cached.data;

    // Dedup concurrent requests
    const pending = this.loadingPromises.get(codINE);
    if (pending) return pending;

    const promise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, promise);

    try {
      const features = await promise;
      this.setCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<OSMSecurityFeature[]> {
    const url = `${this.baseUrl}/osm-security/${codINE}.json.gz`;

    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });

      if (response.status === 404) {
        // Municipio sin instalaciones de seguridad — normal
        return [];
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: OSMSecurityFeature[] = await response.json();
      return data.filter(f => this.isValidCoord(f));
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn(`[OSM_SEC] Timeout cargando ${codINE}`);
      } else {
        console.error(`[OSM_SEC] Error cargando ${codINE}:`, error);
      }
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  /**
   * Geocodifica una infraestructura de seguridad buscando en datos OSM R2.
   * Retorna el mejor resultado o null.
   */
  async geocode(options: OSMSecuritySearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name ?? '');
  }

  /**
   * Busca infraestructuras de seguridad por nombre fuzzy y/o subtipo.
   * Usa campo "tokens" pre-computado como haystack para uFuzzy.
   * Retorna array ordenado por confianza descendente.
   */
  async search(options: OSMSecuritySearchOptions): Promise<OSMSecurityResult[]> {
    const { codINE, name, subtipo, minConfidence = 55, maxResults = 5 } = options;

    const features = await this.loadMunicipio(codINE);
    if (features.length === 0) return [];

    // Filtrar por subtipo si especificado
    let candidates = features;
    if (subtipo) {
      candidates = candidates.filter(
        f => f.subtipo.toUpperCase() === subtipo.toUpperCase()
      );
      if (candidates.length === 0) return [];
    }

    // Sin nombre → devolver candidatos filtrados
    if (!name?.trim()) {
      return candidates
        .slice(0, maxResults)
        .map(f => this.toResult(f, 70));
    }

    // Búsqueda fuzzy usando tokens pre-computados
    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING (usando tokens pre-computados)
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: OSMSecurityFeature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): OSMSecurityResult[] {
    // CLAVE: usar tokens pre-computados como haystack
    const haystack = candidates.map(f => f.tokens);
    const normalizedName = searchName.toLowerCase().trim();
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: OSMSecurityResult[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const fuzzyScore = this.calcFuzzyScore(normalizedName, feature.tokens, feature.nombre);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  /** Calcula score normalizado 0-1 del fuzzy match (contra tokens y nombre) */
  private calcFuzzyScore(needle: string, tokens: string, nombre: string): number {
    const nLow = nombre.toLowerCase();
    const tLow = tokens.toLowerCase();
    if (tLow === needle || nLow === needle) return 1.0;
    if (tLow.includes(needle) || nLow.includes(needle)) return 0.95;
    if (needle.includes(tLow) || needle.includes(nLow)) return 0.90;
    const ratio = needle.length / Math.max(tLow.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  /** Convierte fuzzy score (0-1) a confianza (0-100) — techo 80 por datos OSM */
  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 80;  // OSM max: 80 (datos comunitarios)
    if (score >= 0.85) return 75;
    if (score >= 0.70) return 68;
    if (score >= 0.55) return 60;
    if (score >= 0.40) return 55;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN Y VALIDACIÓN
  // ---------------------------------------------------------------------------

  /** Convierte feature a resultado intermedio con confianza */
  private toResult(
    feature: OSMSecurityFeature,
    confidence: number,
    fuzzyScore?: number,
  ): OSMSecurityResult {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: OSM_SEC_PRECISION_M,
      source: 'osm_security_r2',
      feature,
      fuzzyScore,
      matchedName: feature.nombre,
    };
  }

  /** Convierte resultado OSM a GeocodingResult estándar */
  toGeocodingResult(
    result: OSMSecurityResult,
    _searchName: string,
  ): GeocodingResult {
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'osm_security_r2',
      matchedName: result.matchedName,
    };
  }

  /** Valida que las coordenadas estén dentro del rango esperado (Andalucía UTM30) */
  private isValidCoord(feature: OSMSecurityFeature): boolean {
    return (
      feature.x >= OSM_SEC_X_RANGE.min &&
      feature.x <= OSM_SEC_X_RANGE.max &&
      feature.y >= OSM_SEC_Y_RANGE.min &&
      feature.y <= OSM_SEC_Y_RANGE.max
    );
  }

  // ---------------------------------------------------------------------------
  // CACHÉ LRU
  // ---------------------------------------------------------------------------

  /** Inserta en caché con evicción LRU si se excede el límite */
  private setCache(codINE: string, data: OSMSecurityFeature[]): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      // Evict oldest entry
      let oldestKey: string | null = null;
      let oldestTs = Infinity;
      for (const [key, entry] of this.cache) {
        if (entry.ts < oldestTs) {
          oldestTs = entry.ts;
          oldestKey = key;
        }
      }
      if (oldestKey) this.cache.delete(oldestKey);
    }
    this.cache.set(codINE, { data, ts: Date.now() });
  }

  /** Limpia toda la caché */
  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /** Número de entradas en caché (útil para tests) */
  get cacheSize(): number {
    return this.cache.size;
  }

  /** Carga features directamente en caché sin fetch (útil para tests) */
  loadFromData(codINE: string, features: OSMSecurityFeature[]): void {
    this.setCache(codINE, features);
  }
}
